import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col bg-bone sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl font-light tracking-tight">Your Bag</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-start justify-center gap-4" data-testid="cart-empty-state">
            <p className="font-serif text-2xl italic text-soot">Your bag is empty.</p>
            <Button
              data-testid="cart-continue-shopping-button"
              variant="outline"
              className="rounded-full"
              onClick={() => {
                setIsOpen(false);
                navigate("/shop");
              }}
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-6 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <div className="h-24 w-20 shrink-0 overflow-hidden bg-linen">
                    {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-serif text-lg leading-tight">{i.name}</p>
                        {i.size && <p className="mt-0.5 text-xs text-soot">Size {i.size}</p>}
                      </div>
                      <button
                        data-testid={`cart-remove-${i.product_id}`}
                        aria-label={`Remove ${i.name}`}
                        onClick={() => removeItem(i.key)}
                        className="p-1 text-soot transition-colors hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-seam">
                        <button
                          data-testid={`cart-qty-minus-${i.product_id}`}
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(i.key, i.qty - 1)}
                          className="p-1.5 transition-colors hover:bg-linen"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm">{i.qty}</span>
                        <button
                          data-testid={`cart-qty-plus-${i.product_id}`}
                          aria-label="Increase quantity"
                          onClick={() => updateQty(i.key, i.qty + 1)}
                          className="p-1.5 transition-colors hover:bg-linen"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-seam pt-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-soot">Subtotal</p>
                <p data-testid="cart-subtotal" className="font-serif text-2xl">{inr(subtotal)}</p>
              </div>
              <p className="mt-1 text-xs text-soot">Free shipping above ₹5,000 · otherwise ₹199</p>
              <Button
                data-testid="checkout-button"
                className="mt-4 h-12 w-full rounded-full bg-obsidian text-xs font-bold uppercase tracking-[0.18em] text-bone transition-all duration-300 hover:scale-[1.01] hover:bg-olive"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/checkout");
                }}
              >
                Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
